// 관계도·조직도 엔진(design/relmap.js 포팅). 프레임워크 비의존 — Vue 래퍼(RelMap.vue)가 vault 데이터로 호출한다.
// rel 모드: '나' 중심 궤도(가까울수록 안쪽). org 모드: 부서 상자·직급 줄·보고라인.
// 두 모드 전환 이동 자체가 "공식 라인과 실제 관계는 다르다"는 연출이다.
// 위치는 프레임 수가 아니라 시간 기준으로 보간한다(60/120Hz·느린 기기에서 같은 속도).
const NS = 'http://www.w3.org/2000/svg'

export interface RmPerson { id: string; name: string; title?: string; rel?: string; close: number; c1: string; c2: string; org: [number, number]; boss?: string; company?: string; companyName?: string; companyColor?: string }
export interface RmDeptRect { name: string; x: number; y: number; w: number; h: number; external?: boolean; color?: string }
export interface RmLink { a: string; b: string; type: string; note?: string }
export interface RmData { people: RmPerson[]; depts: RmDeptRect[]; links: RmLink[]; me: { count: number; org: [number, number]; boss?: string } }
export interface RmOpts { onSelect?: (node: any | null, rel?: any[]) => void; onMe?: () => void }

export interface RmApi {
  select(id: string): void
  clear(): void
  grow(by?: number): { count: number; level: number; levelUp: boolean }
  ping(id: string): void
  setMode(m: 'rel' | 'org'): void
  setCalm(v: boolean): void
  destroy(): void
  readonly mode: string
}

export function createRelMap(el: HTMLElement, data: RmData, opts: RmOpts = {}): RmApi {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
  const still = () => reduced || document.documentElement.dataset.motion === 'off'
  el.classList.add('rm')
  el.innerHTML = '<svg viewBox="-200 -200 400 400"><g class="g-dept"></g><g class="g-me"></g><g class="g-tree"></g><g class="g-lk"></g></svg><div class="rm-layer"></div>'
  const svg = el.querySelector('svg')!, layer = el.querySelector('.rm-layer')! as HTMLElement
  const g = (k: string) => svg.querySelector('.g-' + k)!
  const line = (cls: string, parent: string) => { const l = document.createElementNS(NS, 'line'); l.setAttribute('class', cls); g(parent).appendChild(l); return l }

  for (const d of data.depts) {
    const grp = document.createElementNS(NS, 'g'); grp.setAttribute('class', 'dept' + (d.external ? ' ext' : ''))
    if (d.external && d.color) grp.setAttribute('style', `--cc:${d.color}`)
    const label = d.external ? `${d.name} · 고객사` : d.name
    grp.innerHTML = `<rect x="${d.x}" y="${d.y}" width="${d.w}" height="${d.h}" rx="16"/><text x="${d.x + 12}" y="${d.y + 18}">${label}</text>`
    g('dept').appendChild(grp)
  }

  const meNode: any = { id: 'me', name: '나', org: data.me.org, boss: data.me.boss, x: 0, y: 0, cx: 0, cy: 0, ph: 0, sp: 0, isMe: true }
  const N = data.people.length
  const nodes: any[] = data.people.map((p, i) => {
    const a = (i / N) * Math.PI * 2 - Math.PI / 2
    const r = 80 + (1 - p.close) * 92
    const n: any = Object.assign({}, p, { i, rel: [Math.cos(a) * r, Math.sin(a) * r], ph: Math.random() * 6.28, sp: 0.5 + Math.random() * 0.4 })
    ;[n.x, n.y] = n.rel; n.cx = n.x; n.cy = n.y
    n.sz = Math.round(34 + p.close * 14)
    n.el = document.createElement('div')
    n.el.className = 'rm-node' + (p.company ? ' ext' : ''); n.el.dataset.id = p.id
    const relTxt = p.companyName || p.rel || '' // 외부 사람은 소속 회사명을 맥락으로 보여 준다
    const cc = p.companyColor ? `;--cc:${p.companyColor}` : ''
    n.el.innerHTML = `<button type="button" class="rm-n" aria-label="${p.name}" style="--i:${i};--sz:${n.sz}px${cc}"><span class="rm-blob" style="--c1:${p.c1};--c2:${p.c2};--dl:${-i * 1.3}s"></span><span>${p.name}</span><em><b class="r">${relTxt}</b><b class="t">${p.title || ''}</b></em></button>`
    layer.appendChild(n.el)
    n.meLine = line('me-link', 'me')
    return n
  })
  meNode.rel = [0, 0]
  const all = nodes.concat([meNode])
  const byId: Record<string, any> = Object.fromEntries(all.map(n => [n.id, n]))

  const trees = all.filter(n => n.boss && byId[n.boss]).map(n => ({ a: n, b: byId[n.boss], ln: line('tree', 'tree') }))

  const links: any[] = []
  function makeLink(l: RmLink, k: number, isNew?: boolean) {
    const ln = line(`lk ${l.type}${isNew ? ' new' : ''}`, 'lk')
    ln.setAttribute('pathLength', '1')
    ln.style.setProperty('--i', String(k))
    links.push(Object.assign({ ln }, l))
  }
  data.links.filter(l => byId[l.a] && byId[l.b]).forEach((l, k) => makeLink(l, k))

  const me = document.createElement('div')
  me.className = 'rm-me'; layer.appendChild(me); meNode.el = me
  let count = data.me.count
  const meSize = () => Math.round(54 + Math.min(count, 80) * 0.45)
  const level = () => Math.floor(count / 10) + 1
  function renderMe() {
    me.style.setProperty('--s', meSize() + 'px')
    me.innerHTML = `<button type="button" class="rm-mi" aria-label="나"><span class="rm-blob"><span class="rm-eyes"><i></i><i></i></span></span><span>나 · Lv.${level()}</span></button>`
  }
  renderMe()

  let mode: 'rel' | 'org' = 'rel', calm = false
  function setMode(m: 'rel' | 'org') {
    mode = m
    el.classList.toggle('org', m === 'org')
    for (const n of all) [n.tx, n.ty] = m === 'org' ? n.org : n.rel
  }
  setMode('rel')

  let scale = el.clientWidth / 400
  const ro = new ResizeObserver(() => { scale = el.clientWidth / 400; kick() })
  ro.observe(el)

  let last = 0, running = true, dead = false
  function kick() { if (!running) { running = true; last = 0; requestAnimationFrame(frame) } }
  function frame(ts: number) {
    if (dead) return
    const t = ts / 1000
    const dt = last ? Math.min((ts - last) / 1000, 0.5) : 1 / 60
    last = ts
    const k = still() ? 1 : 1 - Math.pow(0.91, dt * 60)
    let moving = 0
    for (const n of all) {
      n.x += (n.tx - n.x) * k; n.y += (n.ty - n.y) * k
      moving = Math.max(moving, Math.abs(n.tx - n.x), Math.abs(n.ty - n.y))
      const f = still() || calm || n.isMe ? 0 : (mode === 'org' ? 1.5 : 4)
      n.cx = n.x + Math.sin(t * n.sp + n.ph) * f
      n.cy = n.y + Math.cos(t * n.sp * 0.8 + n.ph) * f
      n.el.style.transform = `translate(${n.cx * scale}px, ${n.cy * scale}px)`
    }
    const set = (ln: Element, a: any, b: any) => { ln.setAttribute('x1', a.cx); ln.setAttribute('y1', a.cy); ln.setAttribute('x2', b.cx); ln.setAttribute('y2', b.cy) }
    for (const n of nodes) set(n.meLine, meNode, n)
    for (const tr of trees) set(tr.ln, tr.a, tr.b)
    for (const l of links) set(l.ln, byId[l.a], byId[l.b])
    if ((still() || calm) && moving < 0.05) { running = false; return }
    requestAnimationFrame(frame)
  }
  requestAnimationFrame(frame)

  function clear() {
    el.classList.remove('focus')
    el.querySelectorAll('.hot, .sel').forEach(x => x.classList.remove('hot', 'sel'))
    opts.onSelect && opts.onSelect(null)
  }
  function select(id: string) {
    const n = byId[id]; if (!n || n.isMe) return
    clear()
    el.classList.add('focus')
    n.el.classList.add('hot', 'sel'); n.meLine.classList.add('hot')
    const rel: any[] = []
    for (const l of links) {
      if (l.a !== id && l.b !== id) continue
      l.ln.classList.add('hot')
      const other = byId[l.a === id ? l.b : l.a]
      other.el.classList.add('hot')
      rel.push({ person: other, type: l.type, note: l.note })
    }
    opts.onSelect && opts.onSelect(n, rel)
  }
  layer.addEventListener('click', e => {
    const node = (e.target as HTMLElement).closest('.rm-node') as HTMLElement | null
    if (node) { node.classList.contains('sel') ? clear() : select(node.dataset.id!); return }
    if ((e.target as HTMLElement).closest('.rm-me')) { opts.onMe && opts.onMe(); return }
    clear()
  })

  function ping(id: string) {
    const n = byId[id]; if (!n || n.isMe) return
    n.el.classList.remove('ping'); void n.el.offsetWidth; n.el.classList.add('ping')
  }
  function grow(by?: number) {
    const before = level()
    count += by || 1
    renderMe()
    me.classList.remove('pulse', 'happy'); void me.offsetWidth; me.classList.add('pulse', 'happy')
    setTimeout(() => me.classList.remove('happy'), 1400)
    return { count, level: level(), levelUp: level() > before }
  }

  return {
    select, clear, grow, ping,
    setMode(m) { setMode(m); kick() },
    setCalm(v) { calm = !!v; kick() },
    destroy() { dead = true; ro.disconnect() },
    get mode() { return mode },
  }
}
